import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cake, Save, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BirthdayPreferenceCard — lets a signed-in customer set (or clear) the birth
 * date used for birthday promos and their private house number.
 */
export default function BirthdayPreferenceCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [initial, setInitial] = useState({ birthDate: "", houseNumber: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/customer/prefs");
        setBirthDate(data?.birth_date || "");
        setHouseNumber(data?.house_number || "");
        setInitial({
          birthDate: data?.birth_date || "",
          houseNumber: data?.house_number || "",
        });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty =
    birthDate !== initial.birthDate || houseNumber !== initial.houseNumber;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/customer/prefs", {
        birth_date: birthDate || "",
        house_number: houseNumber || "",
      });
      toast.success("Details saved");
      setInitial({ birthDate, houseNumber });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const clearBirthDate = () => setBirthDate("");

  return (
    <Card className="rounded-md border border-border p-6" data-testid="birthday-pref-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <Cake size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl">Birthday & full address</h3>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Add your birth date to receive personalised birthday offers from
            businesses you follow. Your house number stays private — profiles
            publicly show only the street name, city, and state.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="pref-birth"
                className="text-xs font-mono uppercase tracking-wider text-gray-600"
              >
                Birth date
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id="pref-birth"
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="rounded-sm font-mono"
                  data-testid="birthday-pref-date-input"
                />
                {birthDate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearBirthDate}
                    className="rounded-[35px] shrink-0"
                    data-testid="birthday-pref-clear-button"
                    title="Clear birth date"
                  >
                    <Trash2 size={13} />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label
                htmlFor="pref-house"
                className="text-xs font-mono uppercase tracking-wider text-gray-600"
              >
                House number{" "}
                <span className="text-gray-400 font-normal normal-case">
                  (private)
                </span>
              </Label>
              <Input
                id="pref-house"
                placeholder="1500"
                maxLength={20}
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value.slice(0, 20))}
                className="rounded-sm mt-1 font-mono"
                data-testid="birthday-pref-house-input"
              />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="birthday-pref-save-button"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Save size={14} className="mr-1.5" /> Save details
                </>
              )}
            </Button>
            {!dirty && !saving && (
              <span className="text-xs font-mono text-gray-400">
                All changes saved
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
